import { useState, useCallback } from 'react';
import FileUpload from './FileUpload';

const ImageUpload = ({
  onImageSelect,
  onImageRemove,
  maxSize = 5 * 1024 * 1024, // 5MB default for images
  maxFiles = 1,
  multiple = false,
  disabled = false,
  className = '',
  uploadedImages = [],
  loading = false,
  error = null,
  required = false,
  label = 'Upload Images',
  showPreview = true,
  aspectRatio = null, // e.g., '16:9', '1:1', '4:3'
  cropEnabled = false
}) => {
  const [previewImages, setPreviewImages] = useState([]);

  const acceptedTypes = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'image/gif': ['.gif']
  };

  const handleImageSelect = useCallback((files) => {
    const imageFiles = files.map(file => {
      const previewUrl = URL.createObjectURL(file);
      return {
        file,
        preview: previewUrl,
        name: file.name,
        size: file.size,
        type: file.type
      };
    });

    setPreviewImages(prev => [...prev, ...imageFiles]);
    onImageSelect(files);
  }, [onImageSelect]);

  const handleImageRemove = useCallback((index) => {
    // Revoke object URL to prevent memory leaks
    if (previewImages[index]?.preview) {
      URL.revokeObjectURL(previewImages[index].preview);
    }
    
    setPreviewImages(prev => prev.filter((_, i) => i !== index));
    onImageRemove(index);
  }, [previewImages, onImageRemove]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`image-upload-container ${className}`}>
      <FileUpload
        onFileSelect={handleImageSelect}
        onFileRemove={handleImageRemove}
        accept={acceptedTypes}
        maxSize={maxSize}
        maxFiles={maxFiles}
        multiple={multiple}
        disabled={disabled}
        uploadedFiles={uploadedImages}
        loading={loading}
        error={error}
        required={required}
        label={label}
        showPreview={false} // We'll handle our own preview
        helperText={`Supported formats: JPEG, PNG, WebP, GIF • Max size: ${formatFileSize(maxSize)}`}
      >
        {/* Custom upload area for images */}
        <div className="flex flex-col items-center">
          <svg className="w-12 h-12 text-jet-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <p className="text-sm text-jet-gray-600 mb-1">
            Drag & drop images here, or click to select
          </p>
          <p className="text-xs text-jet-gray-500">
            JPEG, PNG, WebP, GIF up to {formatFileSize(maxSize)}
          </p>
        </div>
      </FileUpload>

      {/* Image Preview Grid */}
      {showPreview && (previewImages.length > 0 || uploadedImages.length > 0) && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-jet-gray-700 mb-3">
            {previewImages.length > 0 ? 'Selected Images' : 'Uploaded Images'}
          </h4>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Preview new images */}
            {previewImages.map((image, index) => (
              <div key={`preview-${index}`} className="relative group">
                <div className={`relative overflow-hidden rounded-lg bg-jet-gray-100 ${aspectRatio ? 'aspect-square' : ''}`}>
                  <img
                    src={image.preview}
                    alt={image.name}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Loading overlay */}
                  {loading && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    </div>
                  )}
                  
                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => handleImageRemove(index)}
                    className="absolute top-2 right-2 bg-jet-red-500 hover:bg-jet-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    disabled={loading}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
                
                <div className="mt-2">
                  <p className="text-xs text-jet-gray-600 truncate">{image.name}</p>
                  <p className="text-xs text-jet-gray-500">{formatFileSize(image.size)}</p>
                </div>
              </div>
            ))}

            {/* Show uploaded images */}
            {uploadedImages.map((image, index) => (
              <div key={`uploaded-${index}`} className="relative group">
                <div className={`relative overflow-hidden rounded-lg bg-jet-gray-100 ${aspectRatio ? 'aspect-square' : ''}`}>
                  <img
                    src={image.url || image.preview}
                    alt={image.name || image.originalName}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => onImageRemove(index)}
                    className="absolute top-2 right-2 bg-jet-red-500 hover:bg-jet-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    disabled={loading}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>

                  {/* View full size button */}
                  <button
                    type="button"
                    onClick={() => window.open(image.url || image.preview, '_blank')}
                    className="absolute bottom-2 right-2 bg-jet-blue-500 hover:bg-jet-blue-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                    </svg>
                  </button>
                </div>
                
                <div className="mt-2">
                  <p className="text-xs text-jet-gray-600 truncate">
                    {image.name || image.originalName}
                  </p>
                  <p className="text-xs text-jet-gray-500">
                    {formatFileSize(image.size)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;